import { ButcherHeader } from '@components/ButcherHeader';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { useEffect, useState } from 'react';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useToast } from '@contexts/Toast';
import { useNavigate } from 'react-router-dom';
import { ProductCard } from '@components/OrderCreation/ProductCard';
import {
  CustomizationData,
  MasterPackage,
  MasterProduct,
  NewOrderInitiationProps,
  ProductCustomization,
  ProductEntry,
  SelectedProduct,
} from '@helpers/Models';
import { OrderBreadcrumb } from '@components/OrderCreation/OrderBreadcrumbs';
import { OrderProfileCard } from '@components/OrderCreation/OrderProfileCard';
import { OrderStepper } from '@components/OrderCreation/OrderStepper';
import {
  errorMessage,
  orderBreadCrumbSteps,
  OrderCreationMasterData,
  orderStepper,
} from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Axios } from '@helpers/ApiService';
import { CustomerOrderService } from '@services/CustomerService';

export const ProductDetails: React.FC = () => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();
  const [isFinished, setIsFinished] = useState(false);
  const [masterProducts, setMasterProducts] = useState<MasterProduct[]>([]);
  const [customerId, setCustomerId] = useState<number>(0);
  const [currentCustomizingId, setCurrentCustomizingId] = useState<string>('');
  const [productCustomizations, setProductCustomizations] = useState<
    SelectedProduct[]
  >([]);
  const [customerDetails, setCustomerDetails] =
    useState<NewOrderInitiationProps>();
  const [masterPackages, setMasterPackages] = useState<MasterPackage[]>([]);
  const [tempCustomizations, setTempCustomizations] = useState<
    SelectedProduct[]
  >([]);

  /**
   * Effect to initialize customer and customization identifiers on component mount.
   *
   * - Retrieves the `customerId` using `CustomerOrderService.getCustomerId()`.
   * - If a valid `customerId` is found:
   *   - Updates local state with `setCustomerId`.
   *   - Attempts to retrieve `currentCustomizingId` via `CustomerOrderService.getcurrentCustomizingId()`.
   *   - If available, sets it using `setCurrentCustomizingId`.
   * - If no valid `customerId` is found, navigates to the customer list page.
   *
   * Dependencies:
   * - Runs only once on mount (`[]` dependency array).
   */
  useEffect(() => {
    const customerID = CustomerOrderService.getCustomerId();
    const customerDetails = CustomerOrderService.getCustomerDetails();
    setCustomerDetails(customerDetails);
    if (customerID) {
      setCustomerId(customerID);
      const currentCustomizingId = localStorage.getItem('currentCustomizingId');
      if (currentCustomizingId) {
        setCurrentCustomizingId(currentCustomizingId);
      }
    } else {
      navigate(FrontendRoutes.CUSTOMER_LIST);
    }
  }, []);

  /**
   * Retrieves and parses the saved order data for the current customer and customization ID.
   *
   * - Fetches parsed order data from local storage or a data service.
   * - Checks if there is existing order data for the given `customerId`.
   * - If valid data exists:
   *   - Extracts the relevant customization based on `currentCustomizingId`.
   *   - Marks each product in the customization as selected using its `productId`.
   *   - Tracks the quantity of each product.
   *   - Updates the local `productCustomizations` state with the product list.
   */
  useEffect(() => {
    if (customerId) {
      getOrderData();
      fetchMasterProducts();
      fetchMasterPackages();
    }
  }, [customerId]);

  /**
   * Keeps a temporary copy of the local product customizations in sync with
   * the main `productCustomizations` state.
   *
   * This effect runs whenever `productCustomizations` changes and ensures that
   * `tempCustomizations` always reflects the latest state. It is useful for
   * operations that require a working copy of the data (e.g., temp edits, undo actions)
   * without immediately modifying the original state.
   */
  useEffect(() => {
    setTempCustomizations(productCustomizations);
  }, [productCustomizations]);

  /**
   * Fetches and initializes order customization data for the currently selected customer and species.
   *
   * - Retrieves parsed order data from the `CustomerOrderService`.
   * - Finds the relevant order for the given `customerId` and `currentCustomizingId`.
   * - Extracts product customizations if available and populates:
   *   - `productCustomizations` state with the product list.
   *   - Internal tracking maps (`selected`, `quantities`) for UI management (e.g., product selection and quantity).
   *
   * @returns {void}
   */
  const getOrderData = (): void => {
    const orderData = CustomerOrderService.getParsedOrderData();

    if (orderData && customerId) {
      const customerOrder = orderData[customerId];
      const existingData = customerOrder?.species?.[currentCustomizingId];
      // Tracks selected product IDs to control UI selection state
      const selected: Record<number, boolean> = {};
      // Stores quantity count for each product by product ID
      const quantities: Record<number, number> = {};

      if (existingData?.products && Array.isArray(existingData.products)) {
        existingData.products.forEach((product: ProductEntry) => {
          if (product.productId != null) {
            selected[product.productId] = true;
            quantities[product.productId] = product.quantity ?? 1;
          }
        });
        setProductCustomizations(existingData.products);
      }
    }
  };

  /**
   * Fetches the master products from the API.
   *
   * This function sends a POST request to the `FETCH_SPECIES_MASTERDATA` endpoint to fetch master product data
   * based on a specified `masterDataType` of 4. If the request is successful, the retrieved `masterData` is
   * stored in the `masterProducts` state.
   *
   * If the request fails, it shows an error message using `showToastMessage` and throws the error.
   *
   * @throws {Error} Throws the error encountered during the API request.
   */
  const fetchMasterProducts = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.PRODUCT_MASTER_DATA,
      });
      const masterData = response.data.data.masterData;
      setMasterProducts(masterData);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches master packaging data from the API.
   *
   * Makes a POST request to the `FETCH_SPECIES_MASTERDATA` endpoint with
   * `masterDataType` set to 6 to retrieve the packaging master data.
   *
   * On success, updates the `masterPackages` state with the `packageType` array
   * from the response data.
   * On failure, shows an error toast message and rethrows the error.
   */
  const fetchMasterPackages = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.PACKAGE_MASTER_DATA,
      });
      const masterData = response.data.data.masterData.packageType;
      setMasterPackages(masterData);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Updates the current customization flow state based on the given action.
   *
   * This function toggles the `isFinished` state depending on whether the user
   * wants to move forward ("next") or go back ("back") in the customization steps.
   *
   * @param {string} action - The direction of navigation. Accepts "next" or "back".
   */
  const handleEdit = (action: string): void => {
    switch (action) {
      case 'next':
        setIsFinished(true);
        break;
      case 'back':
        setIsFinished(false);
        break;
    }
  };

  /**
   * Updates the product customizations state with the latest changes from the ProductCard component.
   *
   * @param {CustomizationData[]} updated - The updated list of product customizations.
   *
   * Side Effects:
   * - Replaces the current `productCustomizations` state with the new data.
   */
  const handleProductCardUpdate = (updated: ProductCustomization[]) => {
    setTempCustomizations(updated);
  };

  /**
   * Handles progression and navigation in the order creation workflow.
   *
   * Updates the current customer's species/products data in `localStorage`,
   * applies default packaging if only one master package exists, and
   * navigates to the appropriate route depending on whether the user
   * is saving, going forward, or going back.
   *
   * @param {boolean} isSaveChanges - If true, saves changes without navigating.
   *                                  If false, proceeds to the next or previous step.
   * @param {boolean} [isBackNavigation] - Optional flag. If true and `isSaveChanges` is false,
   *                                       navigates back to the product creation step.
   *
   * @returns {void}
   *
   */
  const handleNext = (
    isSaveChanges: boolean,
    isBackNavigation?: boolean,
  ): void => {
    const orderDataPayload = CustomerOrderService.getParsedOrderData();
    orderDataPayload[customerId].species ??= [];
    while (
      orderDataPayload[customerId].species.length <= currentCustomizingId
    ) {
      orderDataPayload[customerId].species.push({ products: [] });
    }
    const existingSpecies =
      orderDataPayload[customerId].species[currentCustomizingId] ?? {};

    const updatedProduct = tempCustomizations.map((product) => {
      // Converting quantity to number before submitting
      const finalizedProduct = {
        ...product,
        quantity: Number(product.quantity),
      };
      // Adding the default package as a default for all the products
      if (masterPackages.length === 1) {
        return {
          ...finalizedProduct,
          packaging: [
            {
              productPackageTypeId: masterPackages[0].id,
              percentage: 100,
            },
          ],
        };
      }
      return finalizedProduct;
    });
    orderDataPayload[customerId].species[currentCustomizingId] = {
      ...existingSpecies,
      products: updatedProduct,
    };

    localStorage.setItem('orderData', JSON.stringify(orderDataPayload));

    // Handle navigation and save actions
    if (!isSaveChanges) {
      if (isBackNavigation) {
        // Back button navigation
        navigate(FrontendRoutes.CREATE_ORDER_PRODUCT);
      } else if (masterPackages.length === 1) {
        // Navigate to species summary, if theres no other packages except for default
        navigate(FrontendRoutes.SPECIES_SUMMARY);
      } else {
        navigate(FrontendRoutes.CREATE_ORDER_PACKAGING);
      }
    } else {
      // For tracking edit action
      setIsFinished(false);
    }
  };

  // To make track of new changes except the repeat customization, set the data in temp customization
  useEffect(() => {
    setTempCustomizations(productCustomizations);
  }, [productCustomizations]);

  /**
   * Duplicates a product customization in the temporary customization list.
   *
   * This function takes a product customization object and its index in the
   * `tempCustomizations` array, creates a normalized copy of it (ensuring
   * `upchargeIngredientsFlag` is set to 0 if undefined), and inserts the copy
   * immediately **after** the original in the array.
   *
   * After updating, the `tempCustomizations` state is updated with the new array,
   * effectively allowing users to repeat or duplicate a previous customization.
   *
   * @param product - The customization data object to duplicate.
   * @param index - The index of the product in `tempCustomizations` to duplicate after.
   */
  const handleTempRepeatCustomization = (
    product: CustomizationData,
    index: number,
  ) => {
    const newCustomization: ProductCustomization = {
      ...product,
      upchargeIngredientsFlag: product.upchargeIngredientsFlag ?? 0,
    };

    const updated: SelectedProduct[] = [
      ...tempCustomizations.slice(0, index + 1),
      newCustomization,
      ...tempCustomizations.slice(index + 1),
    ];

    setTempCustomizations(updated);
  };

  /**
   * Deletes a product customization from the temporary customization list.
   *
   * This function removes the customization at the specified index from the
   * `tempCustomizations` array and updates the state with the resulting array.
   * It is typically used when a user wants to remove a product from their temp
   * or temporary order.
   *
   * @param index - The index of the product customization to remove from `tempCustomizations`.
   */
  const handleTempDeleteProduct = (index: number) => {
    const updated = tempCustomizations.filter((_, i) => i !== index);
    setTempCustomizations(updated);
  };

  /**
   * Saves the current temp customizations to the main product customizations.
   *
   * This function updates the main `productCustomizations` state with the current
   * `tempCustomizations` (temp changes), effectively committing all edits.
   * After saving, it sets `isFinished` to false to indicate that the editing process
   * is no longer in a "finished" or pending state.
   */
  const handleSaveChanges = () => {
    setProductCustomizations(tempCustomizations);
    setIsFinished(false);
  };

  /**
   * Cancels the current temp edits and restores the previous product customizations.
   *
   * This function resets `tempCustomizations` back to the last saved
   * `productCustomizations`, discarding any unsaved changes made during the temp.
   * It also sets `isFinished` to false to indicate that the editing process
   * is no longer in a "finished" state.
   */
  const handleCancel = () => {
    setTempCustomizations(productCustomizations);
    setIsFinished(false);
  };

  return (
    <div className="flex h-screen w-screen">
      {/* Sidebar */}
      <SideNav active={2} />
      {/* Main Content */}
      <div className="flex-1 overflow-auto relative">
        <div>
          {/* Header */}
          <ButcherHeader />
          {/* Breadcrumbs */}
          <OrderBreadcrumb
            steps={orderBreadCrumbSteps}
            customerName={`${customerDetails?.firstName} ${customerDetails?.lastName}`}
            activeItem="New Order"
            customerId={customerId}
          />
        </div>
        <div className="p-3">
          {/* Customer Profile Card */}
          <OrderProfileCard />
          {/* Stepper */}
          <OrderStepper steps={orderStepper} currentStep={4} />
          <div className="pb-12">
            <div id="product-details-card" className="ml-2 mr-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Product Detail</h3>
                <button
                  type="button"
                  disabled={isFinished}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit('next');
                  }}
                  className={`p-1 ${isFinished ? 'cursor-not-allowed text-[#B4B4B4]' : 'text-primary'}`}
                >
                  <FontAwesomeIcon icon={faPen} />
                  <span className="font-bold ml-2">Edit</span>
                </button>
              </div>
              <div className="flex flex-wrap gap-4">
                {tempCustomizations.length > 0 && masterProducts.length > 0 && (
                  <ProductCard
                    isNext={isFinished}
                    products={tempCustomizations}
                    masterProducts={masterProducts}
                    onUpdate={handleProductCardUpdate}
                    handleRepeatCustomization={handleTempRepeatCustomization}
                    handleDeleteProduct={handleTempDeleteProduct}
                  />
                )}
              </div>
            </div>
            <button
              onClick={() => navigate(FrontendRoutes.CREATE_ORDER_PRODUCT)}
              type="button"
              className="w-full px-6 py-2 mb-3 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
            >
              Add More Products
            </button>
          </div>
          {isFinished ? (
            <div className="flex justify-between gap-4 fixed bottom-0 left-14 w-[calc(100%-64px)] p-4">
              <button
                onClick={handleCancel}
                type="button"
                className="w-[50%] px-6 py-2 border border-primary rounded-sm text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveChanges}
                type="submit"
                className="w-[50%] px-6 py-2 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
              >
                Save Changes
              </button>
            </div>
          ) : (
            <div className="flex justify-between gap-4 fixed bottom-0 left-14 w-[calc(100%-64px)] p-4">
              <button
                onClick={() => handleNext(false, true)} // true is for indicating 'back' action
                type="button"
                className="w-[50%] px-6 py-2 border border-primary rounded-sm text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Back
              </button>
              <button
                onClick={() => handleNext(false)}
                type="submit"
                className="w-[50%] px-6 py-2 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
